import { Component, ElementRef, EventEmitter, Input, OnInit, Output, ViewChild } from '@angular/core';
import { DatePipe } from '@angular/common';
import { CallDetailsAiCoachingTabComponent } from '../call-details-ai-coaching-tab/call-details-ai-coaching-tab.component';
import { SecToStringPipe } from 'src/app/pipes/sec-to-string.pipe';
import html2pdf from 'html2pdf.js';
import * as PDFLib from 'pdf-lib';
import { CallAudioPlayerComponent } from './components/call-audio-player/call-audio-player.component';
import { CallDetails } from '../../models/call-details.model';
import { DiscusionAudio } from '../../models/audio.model';
import { AiScorecardGroup } from '../../models/ai-scorecard-statistic.model';

@Component({
  selector: 'app-call-details',
  templateUrl: './call-details.component.html',
  styleUrls: ['./call-details.component.scss'],
  providers: [SecToStringPipe, DatePipe],
})
export class CallDetailsComponent implements OnInit {
  @ViewChild('scorecardContent', { static: false }) scorecardContent!: ElementRef;
  @ViewChild('scorecardContent2', { static: false }) scorecardContent2!: ElementRef;
  @ViewChild('aiCoachingTab') aiCoachingTab!: CallDetailsAiCoachingTabComponent;
  @ViewChild('leftInfo') leftInfo: ElementRef;
  @ViewChild('rightInfo') rightInfo: ElementRef;
  @ViewChild('audioPlayer') audioPlayer: CallAudioPlayerComponent;

  @Input() callId!: number;
  @Input() set data(payload) {
    this.aiScorecardSections = payload.aiScorecardSections;

    const sections = payload.callDetails.scorecard.criteria.map((s: any) => {
      const section = this.aiScorecardSections
        .filter(i => i.status === 'published')
        .find(i => i.title === s.sectionTitle);
      if (section) {
        s.order = section.order;
        s.criteria = s.criteria.map(c => {
          const criteria = section.AiScorecardCriterias
            .filter(i => i.status === 'published')
            .find(i => i.title === c.criterion);
          if (criteria) {
            c.order = criteria.order;
          } else {
            c.order = 99;
          }
          return c;
        }).sort((a, b) => a.order - b.order);
      } else {
        s.order = 99;
      }
      return s;
    }).sort((a, b) => a.order - b.order);

    this.callDetails = {
      ...payload.callDetails,
      scorecard: {
        ...payload.callDetails.scorecard,
        criteria: sections,
      },
    };

    this.discussionAudio = payload.discussionAudio;
  };

  @Output() doGoBack = new EventEmitter();

  isAudioEnabled = false;

  callDetails: CallDetails;
  discussionAudio: DiscusionAudio;
  aiScorecardSections: AiScorecardGroup[];

  score = 0;
  isTranscriptCopied = false;
  isPdfGenerating = false;

  selectedTab = 'scorecard';

  constructor(
    private secToStringPipe: SecToStringPipe,
    private datePipe: DatePipe
  ) {}

  ngOnInit(): void {
    this.init();
    this.isAudioEnabled = this.callDetails.createdAt > '2025-05-12T20:00:00.000Z';
  }

  ngAfterViewInit(): void {
    const observer = new MutationObserver(() => {
      this.syncHeights();
    });

    observer.observe(this.leftInfo.nativeElement, { childList: true, subtree: true });
  }

  syncHeights() {
    const leftHeight = this.leftInfo.nativeElement.offsetHeight;
    const rightElem = this.rightInfo.nativeElement;

    rightElem.style.maxHeight = `${leftHeight}px`;
    rightElem.style.overflowY = 'auto';
  }

  async init() {
    this.score = Math.round(this.callDetails.score * 100);
  }

  capitalizeStr(str: string) {
    return str.charAt(0).toUpperCase() + str.slice(1);
  }

  goBack() {
    this.doGoBack.emit(true);
  }

  getTranscriptContent() {
    let content = '';

    content += 'Total Duration: ' + this.secToStringPipe.transform(this.callDetails.duration / 1000) + '\n\n';
    content += 'Call ID: ' + this.callDetails.vapiId + '\n\n';
    content += 'Transcript:\n\n';

    for (const message of this.callDetails.transcript) {
      content += '[' + this.secToStringPipe.transform(message.secondsFromStart) + '] ';
      if (message.role === 'bot') {
        content += this.callDetails.agentFirstName + ' ' + this.callDetails.agentLastName;
      } else {
        content += 'Rep';
      }

      content += ': ';
      content += message.message;

      content += '\n\n';
    }

    return content;
  }

  copyTranscriptToClipboard() {
    const content = this.getTranscriptContent();

    const textarea = document.createElement('textarea');
    textarea.value = content;
    document.body.appendChild(textarea);

    textarea.select();
    textarea.setSelectionRange(0, 99999);

    navigator.clipboard.writeText(content)
      .then(() => {
        this.isTranscriptCopied = true;
        setTimeout(() => {
          this.isTranscriptCopied = false;
        }, 3000);
      })
      .catch(err => {
        console.error('Failed to copy text: ', err);
      });

    document.body.removeChild(textarea);
  }

  downloadTxtFile() {
    const content = this.getTranscriptContent();
    const blob = new Blob([content], { type: 'text/plain' });

    const url = window.URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `transcript-${this.callDetails.vapiId}.txt`;
    a.click();

    window.URL.revokeObjectURL(url);
  }

  getFileName = () => {
    const sanitize = (name: string) =>
      name
        .toLowerCase()
        .replace(/[^a-zA-Z0-9]/g, '-')
        .replace(/-+/g, '-')
        .replace(/^-|-$/g, '');

    const formatDate = (date: Date) => this.datePipe.transform(date, 'dd-MM-yyyy') || '';

    return `call-report-${sanitize(this.callDetails.userFirstName)}-${sanitize(this.callDetails.userLastName)}-${formatDate(this.callDetails.createdAt)}-${this.callDetails.id}.pdf`;
  };

  async generatePDF() {
    const element = this.scorecardContent.nativeElement;
    const element2 = this.scorecardContent2.nativeElement;
    const opt = {
      margin: 15,
      filename: this.getFileName(),
      image: { type: 'jpeg', quality: 1 },
      html2canvas: {
        scale: 1.5,
        logging: true,
        useCORS: true,
        allowTaint: true,
        timeout: 30000,
      },
      jsPDF: { unit: 'mm', format: 'a4', orientation: 'portrait' },
      pagebreak: { mode: 'avoid-all', before: '.page-break-before' },
      css_media_type: 'print',
    };
    this.isPdfGenerating = true;
    setTimeout(async () => {
      try {
        const pdf1Blob = await html2pdf().from(element).set(opt).outputPdf('blob');
        const pdf2Blob = await html2pdf().from(element2).set(opt).outputPdf('blob');
        const mergedPdfBlob = await this.mergePDFs(pdf1Blob, pdf2Blob);
        const url = URL.createObjectURL(mergedPdfBlob);
        const a = document.createElement('a');
        a.href = url;
        a.download = opt.filename;
        document.body.appendChild(a);
        a.click();
        document.body.removeChild(a);
      } catch (error) {
        console.error('PDF generation error:', error);
      } finally {
        this.isPdfGenerating = false;
      }
    }, 1000);
  }

  async mergePDFs(pdf1Blob: any, pdf2Blob: any) {
    const pdf1Bytes = await pdf1Blob.arrayBuffer();
    const pdf2Bytes = await pdf2Blob.arrayBuffer();

    const pdfDoc1 = await PDFLib.PDFDocument.load(pdf1Bytes);
    const pdfDoc2 = await PDFLib.PDFDocument.load(pdf2Bytes);
    const mergedPdf = await PDFLib.PDFDocument.create();

    const pages1 = await mergedPdf.copyPages(pdfDoc1, pdfDoc1.getPageIndices());
    pages1.forEach(page => mergedPdf.addPage(page));

    const pages2 = await mergedPdf.copyPages(pdfDoc2, pdfDoc2.getPageIndices());
    pages2.forEach(page => mergedPdf.addPage(page));

    const mergedPdfBytes = await mergedPdf.save();
    return new Blob([mergedPdfBytes], { type: 'application/pdf' });
  }

  onNavigateToAI(data: { sectionTitle: string; criterion: string }) {
    this.selectedTab = 'ai-coaching';
    setTimeout(() => {
      this.aiCoachingTab.scrollAndOpenAccordion(data);
    }, 0);
  }

  protected onJumpToTime(seconds: number) {
    if (this.audioPlayer) {
      this.audioPlayer.seekTo(seconds);
    }
  }
}
